<template>
  <div class="w-full h-full flex-1 flex-col flex min-w-0 gap-4">
    <div class="min-w-0 flex flex-1">
      <div
        class="flex w-full gap-1 items-center p-2 overflow-x-scroll over h-13"
        @wheel="handleBreadcrumbScroll"
      >
        <template v-if="currentRoot && currentRoot !== '1'">
          <button
            @click="isolateBranch('1')"
            class="whitespace-nowrap"
            v-if="currentRoot !== '1'"
          >
            Root
          </button>
          <span class="material-symbols-outlined"> chevron_forward </span>
        </template>
        <template
          v-for="(breadcrumb, index) in breadcrumbs"
          :key="breadcrumb.id"
        >
          <button
            @click="isolateBranch(breadcrumb.id)"
            class="whitespace-nowrap"
          >
            {{ breadcrumb.name }}
          </button>
          <span
            v-if="index < breadcrumbs.length - 1"
            class="material-symbols-outlined"
          >
            chevron_forward
          </span>
        </template>
      </div>
    </div>
    <div class="w-full flex h-full">
      <div class="w-full">
        <div class="relative w-full h-full">
          <Flow :nodes="nodes" :edges="edges">
            <Background />
            <MiniMap :width="300" :height="200" />
            <Controls :showInteractive="false" />
            <div
              class="absolute top-5 right-5 z-10 bg-white shadow flex items-center"
            >
              <button
                v-for="algorithm in [
                  { id: 'mrtree', symbol: 'graph_4' },
                  { id: 'layered', symbol: 'graph_2' },
                ]"
                class="flex items-center p-1"
                :key="algorithm.id"
                :class="{
                  'bg-primary-500 text-white': layoutAlgorithm === algorithm.id,
                }"
                @click="layoutAlgorithm = algorithm.id as Algorithm"
              >
                <span class="material-symbols-outlined">
                  {{ algorithm.symbol }}
                </span>
              </button>
            </div>
          </Flow>
          <LayoutManager ref="layoutManagerRef" :algorithm="layoutAlgorithm" />
        </div>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { Background } from "@vue-flow/background";
import { Controls } from "@vue-flow/controls";
import { MiniMap } from "@vue-flow/minimap";
import { computed, onMounted, ref, useTemplateRef, watch } from "vue";
import { useVueFlow, type Edge, type Node } from "@vue-flow/core";
import LayoutManager from "./LayoutManager.vue";
import Flow from "./Flow.vue";
import { useDebounceFn, useEventListener } from "@vueuse/core";
import type { Element } from "types";
import { getFlattenedSubtree } from "utils";
type Algorithm = "mrtree" | "layered";
const { elements } = defineProps<{ elements: Element[] }>();
const emit = defineEmits<{
  addElement: [parentId: string];
  isolateBranch: [elementId: string];
  updateNodeFigure: [elementId: string, image: File];
  figureClick: [elementId: string];
  elementClick: [elementId: string];
}>();
const nodes = ref<Node[]>([]);
const edges = ref<Edge[]>([]);
const layoutAlgorithm = ref<Algorithm>("mrtree");
const { setCenter, findNode, fitView: vueFlowFitView } = useVueFlow();
const layoutManager = useTemplateRef("layoutManagerRef");

const currentRoot = ref("");

const elementsToDraw = computed(() =>
  currentRoot.value
    ? getFlattenedSubtree(elements, currentRoot.value).map((e) => {
        if (e.id === currentRoot.value) {
          return { ...e, parentElementId: "" };
        }
        return e;
      })
    : [
        ...elements.map((el) => {
          if (!el.parentElementId) {
            return { ...el, parentElementId: "1" };
          }
          return el;
        }),
        { id: "1", name: "Device", parentElementId: "" },
      ]
);

const runLayoutDebounced = useDebounceFn(runLayout, 300);
useEventListener("resize", runLayoutDebounced);

onMounted(runLayout);
watch(() => [elementsToDraw.value, layoutAlgorithm.value], runLayout, {
  deep: true,
});
defineExpose({
  scrollIntoView,
  fitView,
});

async function runLayout() {
  const nodesList: Node[] = [];
  const edgesList: Edge[] = [];
  elementsToDraw.value.forEach((e) => {
    let nodeType = "element";
    if (e.id === "1") {
      nodeType = "root";
    } else if (e.figureUrl) {
      nodeType = "figure";
    }
    nodesList.push({
      id: e.id,
      data: {
        label: e.name,
        onAddElement: () => emit("addElement", e.id),
        onIsolateBranch: () => isolateBranch(e.id),
        onImageLoaded: (file: File) => emit("updateNodeFigure", e.id, file),
        onFigureClick: () => emit("figureClick", e.id),
        onElementClick: () => emit("elementClick", e.id),
        imageUrl: e.figureUrl,
      },
      position: { x: 0, y: 0 },
      type: nodeType,
    });

    if (e.parentElementId) {
      edgesList.push({
        id: "edge-" + Math.random(),
        source: e.parentElementId,
        target: e.id,
        type: "elk-edge",
      });
    }
  });
  const graph = await layoutManager.value!.generateLayout({
    edges: edgesList,
    nodes: nodesList,
  });

  const newNodes: Node[] = [];
  graph.children?.forEach((n) => {
    const elementX = n.x ?? 0;
    const elementY = n.y ?? 0;
    newNodes.push({
      id: n.id,
      position: { x: elementX, y: elementY },
      data: n.data,
      type: n.type,
    });
  });
  nodes.value = newNodes;
  edges.value = graph.edges.map((e) => ({
    id: e.id,
    source: e.sources[0],
    target: e.targets[0],
    type: "elk-edge",
    data: { sections: e.sections },
  }));
}

function scrollIntoView(elementId: string) {
  const node = findNode(elementId);
  if (node) {
    const centerX = node.position.x + node.dimensions.width / 2;
    const centerY = node.position.y + node.dimensions.height / 2;
    setCenter(centerX, centerY, { zoom: 1, duration: 800 });
  }
}

function fitView() {
  vueFlowFitView({
    duration: 300,
  });
}

const breadcrumbs = computed(() =>
  currentRoot.value ? getBreadcrumbs(elements, currentRoot.value) : []
);

async function isolateBranch(elementId: string) {
  currentRoot.value = elementId !== "1" ? elementId : "";
  await new Promise((resolve) => setTimeout(resolve, 300));
  scrollIntoView(elementId);
}

function handleBreadcrumbScroll(event: WheelEvent) {
  const target = event.currentTarget as HTMLElement;
  if (target) {
    target.scrollLeft += event.deltaY;
    event.preventDefault();
  }
}

function getBreadcrumbs(
  elements: Element[],
  startId: string,
  opts: { maxDepth?: number } = {}
): Element[] {
  const { maxDepth = 10_000 } = opts;
  const byId = indexById(elements);

  let cur = byId.get(startId);
  if (!cur) return [];

  const visited = new Set<string>();
  const pathReversed: Element[] = [];

  let steps = 0;
  while (cur && steps < maxDepth) {
    if (visited.has(cur.id)) {
      break;
    }
    visited.add(cur.id);
    pathReversed.push(cur);

    const parentId = cur.parentElementId || "";
    if (!parentId) break;
    cur = byId.get(parentId);
    steps++;
  }

  return pathReversed.reverse();
}
function indexById(elements: Element[]) {
  const byId = new Map<string, Element>();
  for (const el of elements) byId.set(el.id, el);
  return byId;
}
</script>
